import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Reiterleiste, EinsatzFluege, type ReiterEintrag, type EinsatzFlug, Card, Button, Badge, DataTable, TableColumn, Input, Select, PageHeader, useBestaetigung, offeneEinsatzChecklisten, EinsatzDrohnen,
  EinsatzBerichtFelder, EinsatzBerichtAnzeige, PhasenZeitenBlock, EinsatzTruppsBericht, BERICHT_LEER, berichtWerteAus, berichtNutzlast, istBeendetOderAbgeschlossen, type EinsatzBerichtWerte } from '@pilot-hub/ui'
import { missionApi, userApi, api } from '@pilot-hub/api-client'
import { MapPicker } from '../components/MapPicker'
import { EinsatzRisikobewertung } from '../components/EinsatzRisikobewertung'
import { ArrowLeft, Plus, Edit, Trash2, Users, Save } from 'lucide-react'
import {
  kmZugeordnete, kmZeiten, kmMitZeiten, kmTruppText, holeKmData,
  type KMData, type KMZeit,
} from '../utils/kraefteManagement'

interface Einsatz {
  id: number
  titel: string
  typ: string
  status: string
  prioritaet: string
  beginn_geplant: string | null
  alarmierungszeit: string | null
  abfahrt_zeit: string | null
  ankunft_zeit: string | null
  einsatzende_zeit: string | null
  einsatzbereit_zeit: string | null
  einsatzleiter_typ: string | null
  einsatzleiter_nutzer_id: number | null
  einsatzleiter_funkrufname: string | null
  einsatzleiter_funkgruppe: string | null
  einsatzleiter_drohne_nutzer_id?: number | null
  einsatzleiter_drohne_funkrufname?: string | null
  einsatzleiter_name?: string | null
  einsatzort_adresse: string | null
  einsatzort_lat?: number | null
  einsatzort_lon?: number | null
  beschreibung: string | null
  einsatznummer?: string | null
  alarmierende_stelle?: string | null
  einsatzstichwort?: string | null
  alarmierte_schleifen?: string[] | null
  ergebnis?: string | null
  ergebnis_person?: string | null
  ergebnis_text?: string | null
}

const PRIORITAET_OPTIONEN = ['KRITISCH', 'HOCH', 'NORMAL', 'NIEDRIG']
const TYP_OPTIONEN = ['EINSATZ', 'UEBUNGSEINSATZ', 'UEBUNG']

// Status-Optionen pro Einsatz-Typ
const STATUS_NACH_TYP: Record<string, string[]> = {
  EINSATZ: ['ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE', 'EINSATZBEREIT', 'ABGESCHLOSSEN'],
  UEBUNGSEINSATZ: ['GEPLANT', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT', 'EINSATZENDE', 'EINSATZBEREIT', 'ABGESCHLOSSEN'],
  UEBUNG: ['GEPLANT', 'LAUFEND', 'BEENDET', 'ABGESCHLOSSEN'],
}

// Default-Status pro Typ
const DEFAULT_STATUS: Record<string, string> = {
  EINSATZ: 'ALARMIERUNG',
  UEBUNGSEINSATZ: 'GEPLANT',
  UEBUNG: 'GEPLANT',
}

// FastAPI liefert bei 422 ein Array von Fehlerobjekten in "detail" – nie direkt rendern (React-Absturz)
function fehlerText(err: any, fallback: string): string {
  const d = err?.response?.data?.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d)) return d.map((x: any) => `${(x?.loc ?? []).slice(1).join('.')}: ${x?.msg ?? ''}`).join('; ') || fallback
  return fallback
}

const LEER = {
  titel: '',
  typ: 'EINSATZ',
  status: 'ALARMIERUNG',
  prioritaet: 'NORMAL',
  beginn_geplant: '',
  alarmierungszeit: '',
  abfahrt_zeit: '',
  ankunft_zeit: '',
  einsatzende_zeit: '',
  einsatzbereit_zeit: '',
  einsatzleiter_typ: 'NUTZER',
  einsatzleiter_nutzer_id: '',
  einsatzleiter_funkrufname: '',
  einsatzleiter_funkgruppe: '',
  einsatzleiter_drohne_nutzer_id: '',
  einsatzleiter_drohne_funkrufname: '',
  einsatzort_adresse: '',
  beschreibung: ''
}

function getStatusVariant(s: string) {
  switch (s) {
    case 'ALARMIERUNG': return 'info'
    case 'ABFAHRT_WACHE': return 'warning'
    case 'ANKUNFT_EINSATZORT': return 'danger'
    case 'EINSATZENDE': return 'success'
    case 'EINSATZBEREIT': return 'success'
    case 'GEPLANT': return 'info'
    case 'LAUFEND': return 'danger'
    case 'BEENDET': return 'warning'
    case 'ABGESCHLOSSEN': return 'success'
    default: return 'info'
  }
}

function getPriorityVariant(p: string) {
  switch (p) {
    case 'KRITISCH': return 'danger'
    case 'HOCH': return 'warning'
    case 'NORMAL': return 'info'
    case 'NIEDRIG': return 'success'
    default: return 'info'
  }
}

type EinsatzReiter = 'uebersicht' | 'zeiten' | 'kraefte' | 'fluege' | 'risiko'

/** Reiter, in dem ein Fehler (Feldname oder Meldungstext) liegt */
function reiterFuerFehler(feld: string): EinsatzReiter {
  if (/einsatznummer|alarmierende|stichwort|schleife|ergebnis|zeit|alarmierung|abfahrt|ankunft|einsatzende|einsatzbereit|phase/i.test(feld)) return 'zeiten'
  return 'uebersicht'
}

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; einsatzId?: number }

export function MissionsPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const [einsaetze, setEinsaetze] = useState<Einsatz[]>([])
  const [leiter, setLeiter] = useState<Array<{ id: number | string; label: string }>>([])
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)

  const [view, setView] = useState<ViewState>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ ...LEER })
  const [speichert, setSpeichert] = useState(false)
  const [bericht, setBericht] = useState<EinsatzBerichtWerte>({ ...BERICHT_LEER })
  const [reiter, setReiter] = useState<EinsatzReiter>('uebersicht')
  const [fehlerReiter, setFehlerReiter] = useState<EinsatzReiter | null>(null)
  const waehleReiter = (k: EinsatzReiter) => { setReiter(k); setFehlerReiter(f => (f === k ? null : f)) }

  // ---- Flüge dieses Einsatzes, Drohnen-Anzahl und Drohnen-Namen (für den Reiter-Zähler)
  const [flugDaten, setFlugDaten] = useState<{ id: number; items: EinsatzFlug[] } | null>(null)
  const [flugLaedt, setFlugLaedt] = useState(false)
  const [flugFehler, setFlugFehler] = useState<string | null>(null)
  const [drohnenNamen, setDrohnenNamen] = useState<Record<number, string> | null>(null)
  const [drohnenAnzahl, setDrohnenAnzahl] = useState(0)

  // ---- Kräfte Management: zugeteilte Einsatzkräfte dieses Einsatzes
  const [kmData, setKmData] = useState<KMData | null>(null)
  const [kmZeitenState, setKmZeitenState] = useState<Record<number, KMZeit>>({})
  const [kmLaedt, setKmLaedt] = useState(false)
  const [kmFehler, setKmFehler] = useState<string | null>(null)
  const [alleNutzer, setAlleNutzer] = useState<Array<{ id: number; name: string }>>([])

  const kraftName = useCallback((id: number) => {
    const u = alleNutzer.find(x => x.id === id)
    return u ? u.name : `Nutzer #${id}`
  }, [alleNutzer])

  const zugeordnete = useMemo(() => kmZugeordnete(kmData), [kmData])

  // Fahrzeugnamen (Funkrufname bzw. Kennzeichen) für die Anzeige der Besatzungszuordnung
  const [fahrzeugNamen, setFahrzeugNamen] = useState<Record<number, string>>({})
  useEffect(() => {
    if (zugeordnete.length === 0 || Object.keys(fahrzeugNamen).length > 0) return
    api.get('/fahrzeuge', { params: { size: 500 } }).then(r => {
      const m: Record<number, string> = {}
      for (const f of ((r.data as any)?.items ?? []) as any[]) {
        m[f.id] = String(f.rufzeichen || f.kennzeichen || f.fahrzeugtyp || f.typ || `Fahrzeug #${f.id}`)
      }
      setFahrzeugNamen(m)
    }).catch(() => {})
  }, [zugeordnete.length, fahrzeugNamen])

  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const [e, l, alle] = await Promise.all([
        missionApi.list({ size: 500 }),
        userApi.list({ size: 500, rolle: 'FUEHRUNG' }),
        userApi.list({ size: 500 })
      ])
      setEinsaetze((e.data as any).items ?? [])
      setAlleNutzer(((alle.data as any).items ?? []).map((u: any) => ({
        id: u.id,
        name: `${u.vorname ?? ''} ${u.nachname ?? ''}`.trim() || u.benutzername
      })))
      const fuehrungskraefte = ((l.data as any).items ?? []).map((u: any) => ({
        id: u.id,
        label: `${[u.vorname, u.nachname].filter(Boolean).join(' ') || u.benutzername} (${u.benutzername})`
      }))
      setLeiter([
        { id: 'FEUERWEHR', label: 'Feuerwehr' },
        { id: 'POLIZEI', label: 'Polizei' },
        { id: 'THW', label: 'THW' },
        { id: 'DLRG', label: 'DLRG' },
        { id: 'ELW', label: 'ELW' },
        ...fuehrungskraefte.map((u: any) => ({ id: u.id, label: u.label }))
      ])
    } catch (err: any) {
      setFehler(fehlerText(err, 'Einsätze konnten nicht geladen werden'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])

  // Direkter Weg aus dem Dashboard (z. B. Ergebnis eintragen): /einsaetze?bearbeiten=<id> öffnet diesen Einsatz zum Bearbeiten
  const [suchParams, setSuchParams] = useSearchParams()
  useEffect(() => {
    const id = Number(suchParams.get('bearbeiten'))
    if (!id || loading) return
    const e = einsaetze.find(x => x.id === id)
    if (e) oeffneBearbeiten(e, 'zeiten')
    else setFehler('Der Einsatz wurde nicht gefunden.')
    setSuchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, einsaetze, suchParams])

  // Zugeordnete Einsatzkräfte (Kräfte Management) laden, sobald ein Einsatz geöffnet wird.
  // Retry: kurz nach einem Token-Refresh (401) oder Container-Neustart (502) klappt es erneut.
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setKmData(null); setKmZeitenState({}); setKmFehler(null); return }
    let cancelled = false
    setKmLaedt(true); setKmFehler(null)
    ;(async () => {
      for (let versuch = 0; versuch < 3; versuch++) {
        try {
          const r = await api.get(`/kraefte-management/${id}`)
          if (cancelled) return
          const d: KMData = holeKmData(r)
          setKmData(d); setKmZeitenState(kmZeiten(d)); setKmFehler(null)
          break
        } catch {
          if (cancelled) return
          if (versuch === 2) {
            setKmData(null); setKmZeitenState({})
            setKmFehler('Einsatzkräfte konnten nicht geladen werden.')
          } else {
            await new Promise(res => setTimeout(res, 600))
          }
        }
      }
      if (!cancelled) setKmLaedt(false)
    })()
    return () => { cancelled = true }
  }, [view.einsatzId])

  // Flüge des Einsatzes (lesend); beim Öffnen des Reiters „Flüge“ erneut laden
  const fluegeOffen = reiter === 'fluege'
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setFlugDaten(null); setFlugFehler(null); return }
    let cancelled = false
    setFlugLaedt(true)
    api.get('/fluege', { params: { einsatz_id: id, size: 500 } })
      .then(r => { if (!cancelled) { setFlugDaten({ id, items: (r.data as any)?.items ?? [] }); setFlugFehler(null) } })
      .catch(() => { if (!cancelled) setFlugFehler('Flüge konnten nicht geladen werden.') })
      .finally(() => { if (!cancelled) setFlugLaedt(false) })
    return () => { cancelled = true }
  }, [view.einsatzId, fluegeOffen])
  const fluege = flugDaten && flugDaten.id === view.einsatzId ? flugDaten.items : []

  // Drohnen-Namen einmalig, sobald Flüge angezeigt werden
  useEffect(() => {
    if (!fluegeOffen || drohnenNamen) return
    api.get('/drohnen', { params: { size: 500 } }).then(r => {
      const m: Record<number, string> = {}
      for (const x of ((r.data as any)?.items ?? []) as any[]) {
        m[x.id] = [x.asb_inventarnummer, [x.hersteller, x.modell].filter(Boolean).join(' ')].filter(Boolean).join(' – ') || x.kennzeichen || `Drohne #${x.id}`
      }
      setDrohnenNamen(m)
    }).catch(() => setDrohnenNamen({}))
  }, [fluegeOffen, drohnenNamen])

  // Anzahl der Drohnen im Einsatz (Zähler im Reiter „Kräfte & Drohnen“)
  useEffect(() => {
    const id = view.einsatzId
    if (!id) { setDrohnenAnzahl(0); return }
    let cancelled = false
    api.get(`/einsaetze/${id}/drohnen`)
      .then(r => { if (!cancelled) setDrohnenAnzahl(Array.isArray(r.data) ? r.data.length : 0) })
      .catch(() => { if (!cancelled) setDrohnenAnzahl(0) })
    return () => { cancelled = true }
  }, [view.einsatzId, reiter])

  const einsatzleiterLabel = (e: Einsatz) => {
    if (!e.einsatzleiter_typ) return '—'
    if (e.einsatzleiter_typ === 'NUTZER' && e.einsatzleiter_nutzer_id) {
      const u = leiter.find(x => x.id === e.einsatzleiter_nutzer_id)
      return u ? u.label : `Nutzer #${e.einsatzleiter_nutzer_id}`
    }
    const labels: Record<string, string> = { FEUERWEHR: 'Feuerwehr', POLIZEI: 'Polizei', THW: 'THW', DLRG: 'DLRG', ELW: 'ELW', NUTZER: 'Nutzer' }
    return labels[e.einsatzleiter_typ] || e.einsatzleiter_typ
  }

  function oeffneNeu() {
    setReiter('uebersicht'); setFehlerReiter(null); setFehler(null)
    setForm({ ...LEER, status: DEFAULT_STATUS[LEER.typ], prioritaet: 'NORMAL', typ: 'EINSATZ', einsatzleiter_typ: 'NUTZER' })
    setBericht({ ...BERICHT_LEER })
    setView({ mode: 'create' })
  }

  function formAusEinsatz(e: Einsatz): Record<string, string> {
    return {
      titel: e.titel ?? '',
      typ: e.typ ?? 'EINSATZ',
      status: e.status ?? DEFAULT_STATUS[e.typ ?? 'EINSATZ'],
      prioritaet: e.prioritaet ?? 'NORMAL',
      beginn_geplant: e.beginn_geplant ? e.beginn_geplant.slice(0, 16) : '',
      alarmierungszeit: e.alarmierungszeit ? e.alarmierungszeit.slice(0, 16) : '',
      abfahrt_zeit: e.abfahrt_zeit ? e.abfahrt_zeit.slice(0, 16) : '',
      ankunft_zeit: e.ankunft_zeit ? e.ankunft_zeit.slice(0, 16) : '',
      einsatzende_zeit: e.einsatzende_zeit ? e.einsatzende_zeit.slice(0, 16) : '',
      einsatzbereit_zeit: e.einsatzbereit_zeit ? e.einsatzbereit_zeit.slice(0, 16) : '',
      einsatzleiter_typ: e.einsatzleiter_typ ?? 'NUTZER',
      einsatzleiter_nutzer_id: e.einsatzleiter_nutzer_id ? String(e.einsatzleiter_nutzer_id) : '',
      einsatzleiter_funkrufname: e.einsatzleiter_funkrufname ?? '',
      einsatzleiter_funkgruppe: e.einsatzleiter_funkgruppe ?? '',
      einsatzleiter_drohne_nutzer_id: e.einsatzleiter_drohne_nutzer_id ? String(e.einsatzleiter_drohne_nutzer_id) : '',
      einsatzleiter_drohne_funkrufname: e.einsatzleiter_drohne_funkrufname ?? '',
      einsatzort_adresse: e.einsatzort_adresse ?? '',
      beschreibung: e.beschreibung ?? ''
    }
  }

  function oeffneBearbeiten(e: Einsatz, ziel?: EinsatzReiter) {
    // Wechsel Ansicht -> Bearbeiten desselben Einsatzes behält den Reiter, sonst „Übersicht“ (oder das Ziel)
    setReiter(ziel ?? (view.mode === 'detail' && view.einsatzId === e.id ? reiter : 'uebersicht')); setFehlerReiter(null)
    setForm(formAusEinsatz(e))
    setBericht(berichtWerteAus(e))
    setView({ mode: 'edit', einsatzId: e.id })
  }

  function oeffneDetail(e: Einsatz) {
    setReiter('uebersicht'); setFehlerReiter(null)
    // Formularwerte des angeklickten Einsatzes setzen (sonst bleiben Werte des zuletzt geöffneten Einsatzes stehen)
    setForm(formAusEinsatz(e))
    setBericht(berichtWerteAus(e))
    setView({ mode: 'detail', einsatzId: e.id })
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ ...LEER, status: DEFAULT_STATUS[LEER.typ], prioritaet: 'NORMAL', typ: 'EINSATZ', einsatzleiter_typ: 'NUTZER' })
  }

  async function speichern() {
    if (!form.titel.trim()) { setFehler('Bitte einen Titel angeben (Reiter „Übersicht“).'); setFehlerReiter('uebersicht'); setReiter('uebersicht'); return }
    setFehler(null); setFehlerReiter(null)
    // Beenden (UEBUNG: Status BEENDET, sonst Phase EINSATZENDE): aktiv nachfragen, wenn Einsatz-Checklisten offen sind
    const beendet = view.mode === 'edit' && view.einsatzId && (form.typ === 'UEBUNG' ? form.status === 'BEENDET' : form.status === 'EINSATZENDE')
    if (beendet) {
      try {
        const cl = await api.get(`/einsaetze/${view.einsatzId}/checklisten`)
        const offen = offeneEinsatzChecklisten((cl.data as any[]) ?? [], { typ: form.typ, ohnePhase: form.typ === 'UEBUNG' ? undefined : 'EINSATZENDE' })
        if (offen.length) {
          const ok = await bestaetigen({
            titel: form.typ === 'UEBUNG' ? 'Übung wirklich beenden?' : 'Einsatz wirklich beenden?',
            text: `Es ${offen.length === 1 ? 'ist noch 1 Checkliste' : `sind noch ${offen.length} Checklisten`} offen:`,
            hinweis: <ul className="list-disc pl-5">{offen.map(o => <li key={o.instanz.id}>{o.text}</li>)}</ul>,
            bestaetigenText: 'Trotzdem beenden', gefahr: true,
          })
          if (!ok) return
        }
      } catch { /* Checklisten nicht ladbar: Speichern nicht blockieren */ }
    }
    // Beenden/Abschließen ohne Ergebnis: nur ein weicher Hinweis (kein Pflichtfeld, später nachtragbar)
    const vorher = view.einsatzId ? einsaetze.find(x => x.id === view.einsatzId) : null
    if (istBeendetOderAbgeschlossen(form.status) && !istBeendetOderAbgeschlossen(vorher?.status) && !bericht.ergebnis) {
      const ok = await bestaetigen({
        titel: 'Ohne Ergebnis abschließen?',
        text: 'Für diesen Einsatz ist noch kein Ergebnis eingetragen (z. B. Gefunden, Fehlalarm).',
        hinweis: 'Das Ergebnis ist freiwillig und lässt sich auch nach dem Abschluss eintragen.',
        bestaetigenText: 'Trotzdem speichern', abbrechenText: 'Zurück', gefahr: false,
      })
      if (!ok) return
    }
    setSpeichert(true)
    try {
      const payload: any = {
        ...berichtNutzlast(bericht),
        titel: form.titel,
        typ: form.typ,
        status: form.status,
        prioritaet: form.prioritaet,
        beginn_geplant: form.beginn_geplant || null,
        alarmierungszeit: form.alarmierungszeit || null,
        abfahrt_zeit: form.abfahrt_zeit || null,
        ankunft_zeit: form.ankunft_zeit || null,
        einsatzende_zeit: form.einsatzende_zeit || null,
        einsatzbereit_zeit: form.einsatzbereit_zeit || null,
        einsatzleiter_typ: form.einsatzleiter_typ || 'NUTZER',
        einsatzleiter_nutzer_id: form.einsatzleiter_nutzer_id ? Number(form.einsatzleiter_nutzer_id) : null,
        einsatzleiter_funkrufname: form.einsatzleiter_funkrufname || null,
        einsatzleiter_funkgruppe: form.einsatzleiter_funkgruppe || null,
        einsatzleiter_drohne_nutzer_id: form.einsatzleiter_drohne_nutzer_id ? Number(form.einsatzleiter_drohne_nutzer_id) : null,
        einsatzleiter_drohne_funkrufname: form.einsatzleiter_drohne_funkrufname || null,
        einsatzort_adresse: form.einsatzort_adresse || null,
        beschreibung: form.beschreibung || null
      }
      if (view.mode === 'edit' && view.einsatzId) await missionApi.update(view.einsatzId, payload)
      else await missionApi.create(payload)
      // Einsatzzeiten der zugeteilten Kräfte (Kräfte Management) mitspeichern
      if (view.mode === 'edit' && view.einsatzId && zugeordnete.length > 0) {
        await api.put(`/kraefte-management/${view.einsatzId}`, {
          data: kmMitZeiten(kmData, kmZeitenState),
        })
      }
      zurueckZurListe()
      await laden()
    } catch (err: any) {
      const text = fehlerText(err, 'Speichern fehlgeschlagen')
      setFehler(text)
      const d = err?.response?.data?.detail
      const feld = Array.isArray(d) ? String(d[0]?.loc?.slice(-1)[0] ?? '') : text
      const ziel = reiterFuerFehler(feld)
      setFehlerReiter(ziel); setReiter(ziel)
    } finally {
      setSpeichert(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await missionApi.delete(id)
      await laden()
    } catch (err: any) {
      setFehler(fehlerText(err, 'Löschen fehlgeschlagen'))
    }
  }

  // Dynamische Status-Optionen basierend auf ausgewähltem Typ
  const aktuelleStatusOptionen = useMemo(() => STATUS_NACH_TYP[form.typ] || STATUS_NACH_TYP.EINSATZ, [form.typ])

  const gefiltert = useMemo(() => einsaetze.filter(e =>
    (statusFilter === 'all' || e.status === statusFilter) &&
    (e.titel.toLowerCase().includes(search.toLowerCase()) ||
     (e.typ ?? "").toLowerCase().includes(search.toLowerCase()))
  ), [einsaetze, search, statusFilter])

  const columns: TableColumn[] = [
    { key: 'titel', header: 'Titel', accessor: 'titel' },
    { key: 'typ', header: 'Typ', accessor: 'typ' },
    { key: 'status', header: 'Status', accessor: 'status', render: (v: any) => <Badge variant={getStatusVariant(v)}>{v}</Badge> },
    { key: 'prioritaet', header: 'Priorität', accessor: 'prioritaet', render: (v: any) => <Badge variant={getPriorityVariant(v)}>{v}</Badge> },
    { key: 'beginn', header: 'Geplant', accessor: 'beginn_geplant', render: (v: any) => v ? new Date(v).toLocaleString('de-DE') : '—' },
    { key: 'leiter', header: 'Einsatzleitung', accessor: 'id', render: (_v: any, e: any) => einsatzleiterLabel(e) },
    { key: 'ort', header: 'Ort', accessor: 'einsatzort_adresse', render: (_v: any, e: any) => e.einsatzort_adresse || '—' },
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, row: any) => (
        <div className="flex items-center gap-3">
          <button type="button" aria-label="Einsatz bearbeiten" onClick={() => oeffneBearbeiten(row)} className="flex items-center justify-center w-8 h-8 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 rounded-lg text-blue-400 hover:bg-blue-400/10 hover:text-blue-300 transition-colors" title="Einsatz bearbeiten"><Edit className="h-4 w-4" /></button>
          <button type="button" onClick={async () => { if (await bestaetigen({ titel: 'Einsatz löschen?', text: 'Einsatz', objekt: String(row.titel), hinweis: 'Alle Flüge dieses Einsatzes werden mit gelöscht (inkl. Telemetrie, Checklisten, Kräfte-Management, Taktische Karte und Freigabelink). Das kann nicht rückgängig gemacht werden.' })) missionApi.delete(row.id).then(laden).catch((err: any) => setFehler(fehlerText(err, 'Löschen fehlgeschlagen'))) }} aria-label="Einsatz löschen" className="flex items-center justify-center w-8 h-8 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 rounded-lg text-red-400 hover:bg-red-400/10 hover:text-red-300 transition-colors" title="Einsatz löschen (mit allen Flügen)"><Trash2 className="h-4 w-4" /></button>
        </div>
      )
    },
  ]

  // ====== LIST VIEW ======
  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        {dialog}
        <PageHeader
          title=""
          subtitle="Alle Einsätze der ASB Drohnenstaffel verwalten"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neuer Einsatz</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        <Card>
          <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Input placeholder="Nach Titel oder Typ suchen..." value={search} onChange={e => setSearch(e.target.value)} className="w-full sm:w-72" />
            <Select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              options={[{ value: 'all', label: 'Alle Status' }, ...Object.values(STATUS_NACH_TYP).flat().filter((v, i, a) => a.indexOf(v) === i).map(s => ({ value: s, label: s }))]}
              placeholder="— Status —" className="w-full sm:w-48" />
            <div className="shrink-0 whitespace-nowrap text-sm text-dark-text-muted" data-testid="einsatz-zaehler">{gefiltert.length} von {einsaetze.length}</div>
          </div>

          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Einsätze…</div> : (
            <DataTable data={gefiltert} columns={columns} onRowClick={oeffneDetail} keyAccessor={(e: any) => String(e.id)} emptyMessage="Keine Einsätze vorhanden" />
          )}
        </Card>
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW (Reiter) ======
  const currentEinsatz = view.einsatzId ? einsaetze.find(e => e.id === view.einsatzId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  const kopfBeginn = form.beginn_geplant
    ? new Date(form.beginn_geplant).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—'
  const beginnLabel = ['LAUFEND', 'ALARMIERUNG', 'ABFAHRT_WACHE', 'ANKUNFT_EINSATZORT'].includes(form.status) ? 'Einsatzbeginn' : 'Geplant am'
  const kraefteAnzahl = zugeordnete.length + drohnenAnzahl
  const reiterListe: Array<ReiterEintrag<EinsatzReiter>> = ([
    { key: 'uebersicht', label: 'Übersicht' },
    { key: 'zeiten', label: 'Zeiten & Bericht' },
    { key: 'kraefte', label: isCreate ? 'Kräfte & Drohnen' : `Kräfte & Drohnen (${kraefteAnzahl})` },
    ...(!isCreate ? [
      { key: 'fluege' as const, label: `Flüge (${fluege.length})` },
      { key: 'risiko' as const, label: 'Risikobewertung' },
    ] : []),
  ] as Array<ReiterEintrag<EinsatzReiter>>).map(r => ({ ...r, fehler: fehlerReiter === r.key }))
  // Optionen mit aktuellem Wert auch dann, wenn die Person nicht in der Liste der Führungskräfte steht
  const leiterOptionen = (wert: string) => {
    const o = leiter.filter(l => typeof l.id === 'number').map(u => ({ value: String(u.id), label: u.label }))
    if (wert && !o.some(x => x.value === wert)) o.push({ value: wert, label: kraftName(Number(wert)) })
    return o
  }

  return (
    <div className="space-y-4">
      {dialog}
      {/* Kopfzeile: immer sichtbar */}
      <div className="z-20 space-y-3 bg-dark-bg pb-2 md:sticky md:top-0" data-testid="einsatz-kopf">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            {isDetail && currentEinsatz && <Button data-testid="einsatz-bearbeiten" onClick={() => oeffneBearbeiten(currentEinsatz)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
            <span className="text-sm text-dark-text-muted">{isCreate ? 'Neuer Einsatz' : isEdit ? 'Einsatz bearbeiten' : 'Einsatz anzeigen'}</span>
          </div>
          <div className="flex items-center gap-2">
            {!isDetail && <Button onClick={speichern} disabled={speichert}><Save className="h-4 w-4 mr-1" />{speichert ? 'Speichern…' : 'Speichern'}</Button>}
          </div>
        </div>
        <Card className="!py-3">
          <h1 className="break-words text-xl font-bold text-dark-text" data-testid="kopf-titel">{form.titel || (isCreate ? 'Neuer Einsatz' : '—')}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
            <span className="text-dark-text-muted">Typ: <span className="text-dark-text">{form.typ || '—'}</span></span>
            <Badge variant={getStatusVariant(form.status)}>{form.status}</Badge>
            <Badge variant={getPriorityVariant(form.prioritaet)}>{form.prioritaet}</Badge>
            <span className="text-dark-text-muted">{beginnLabel}: <span className="text-dark-text">{kopfBeginn}</span></span>
            <span className="min-w-0 break-words text-dark-text-muted">Einsatzort: <span className="text-dark-text">{form.einsatzort_adresse || '—'}</span></span>
          </div>
        </Card>
        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200" data-testid="einsatz-fehler">{fehler}</div>}
        <Reiterleiste reiter={reiterListe} aktiv={reiter} onWechsel={waehleReiter} testid="einsatz-reiter" />
      </div>

      <Card>
        <div className="space-y-4">
          {reiter === 'uebersicht' && (
            <>
              <div>
                <label className="label">Titel *</label>
                <Input value={form.titel} onChange={e => setForm({...form, titel: e.target.value})} disabled={isDetail} placeholder="z.B. Waldbrand Eberstadt" />
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="label">Typ *</label>
                  <Select value={form.typ} onChange={e => { const val = e.target.value; setForm({...form, typ: val, status: DEFAULT_STATUS[val] })}} disabled={isDetail}
                    options={TYP_OPTIONEN.map(t => ({ value: t, label: t }))} placeholder="— Typ wählen —" />
                </div>
                <div>
                  <label className="label">Status *</label>
                  <Select value={form.status} onChange={e => setForm({...form, status: e.target.value})} disabled={isDetail}
                    options={aktuelleStatusOptionen.map(s => ({ value: s, label: s }))} placeholder="— Status wählen —" />
                </div>
                <div>
                  <label className="label">Priorität *</label>
                  <Select value={form.prioritaet} onChange={e => setForm({...form, prioritaet: e.target.value})} disabled={isDetail}
                    options={PRIORITAET_OPTIONEN.map(p => ({ value: p, label: p }))} placeholder="— Priorität —" />
                </div>
                <div>
                  <label className="label">{beginnLabel}</label>
                  <Input type="datetime-local" value={form.beginn_geplant} onChange={e => setForm({...form, beginn_geplant: e.target.value})} disabled={isDetail} className="w-full" />
                </div>
              </div>
              <div>
                <label className="label">Einsatzort</label>
                <MapPicker
            value={form.einsatzort_adresse}
            onChange={(val: string) => setForm({...form, einsatzort_adresse: val})}
            lat={currentEinsatz?.einsatzort_lat}
            lon={currentEinsatz?.einsatzort_lon}
            placeholder="GPS-Koordinaten, Adresse oder Karten-Pin"
            disabled={isDetail}
          />
              </div>
              <div>
                <label className="label">Beschreibung</label>
                <textarea value={form.beschreibung} onChange={e => setForm({...form, beschreibung: e.target.value})} disabled={isDetail} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400" />
              </div>
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="min-w-0">
                    <label className="label">Einsatzleitung</label>
                    <Select value={form.einsatzleiter_typ} onChange={e => { const val = e.target.value; setForm({...form, einsatzleiter_typ: val, einsatzleiter_nutzer_id: '' })}} disabled={isDetail}
                      options={[
                        { value: 'FEUERWEHR', label: 'Feuerwehr' },
                        { value: 'POLIZEI', label: 'Polizei' },
                        { value: 'THW', label: 'THW' },
                        { value: 'DLRG', label: 'DLRG' },
                        { value: 'ELW', label: 'ELW' },
                        { value: 'NUTZER', label: 'Führungskraft (aus Liste)' }
                      ]} placeholder="— Einsatzleitung wählen —" />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkrufname der Einsatzleitung</label>
                    <Input value={form.einsatzleiter_funkrufname} onChange={e => setForm({...form, einsatzleiter_funkrufname: e.target.value})} disabled={isDetail} placeholder="z.B. Florian 10/1, ELW 1, etc." />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkgruppe</label>
                    <Input value={form.einsatzleiter_funkgruppe} onChange={e => setForm({...form, einsatzleiter_funkgruppe: e.target.value})} disabled={isDetail} placeholder="z.B. 4m, 2m, Kanal 1, etc." />
                  </div>
                </div>
                {form.einsatzleiter_typ === 'NUTZER' && (
                  <div>
                    <label className="label">Führungskraft</label>
                    <Select value={form.einsatzleiter_nutzer_id} onChange={e => setForm({...form, einsatzleiter_nutzer_id: e.target.value})} disabled={isDetail}
                      options={leiterOptionen(form.einsatzleiter_nutzer_id)}
                      placeholder="— Führungskraft wählen —" />
                  </div>
                )}
                {/* EGRED 2: Einsatzleiter Drohne */}
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div className="min-w-0">
                    <label className="label">Einsatzleiter Drohne (Gruppenführer / Abschnittsleiter)</label>
                    <Select value={form.einsatzleiter_drohne_nutzer_id} onChange={e => setForm({...form, einsatzleiter_drohne_nutzer_id: e.target.value})} disabled={isDetail}
                      options={leiterOptionen(form.einsatzleiter_drohne_nutzer_id)}
                      placeholder="— Führungskraft wählen —" />
                  </div>
                  <div className="min-w-0">
                    <label className="label">Funkrufname Einsatzleiter Drohne</label>
                    <Input value={form.einsatzleiter_drohne_funkrufname} onChange={e => setForm({...form, einsatzleiter_drohne_funkrufname: e.target.value})} disabled={isDetail} placeholder="z.B. Drohne 1, UAS-Führer" />
                  </div>
                </div>
              </div>
            </>
          )}

          {reiter === 'zeiten' && (
            <>
              {isDetail && currentEinsatz && (
                <>
                  {currentEinsatz.typ && <PhasenZeitenBlock nurLesen={currentEinsatz.typ === 'UEBUNG'} einsatz={currentEinsatz} api={api} onGeaendert={() => { void laden() }} />}
                  <div className="border-t border-dark-border pt-4">
                    <h3 className="mb-2 text-sm font-semibold text-dark-text">Angaben für den Einsatzbericht</h3>
                    <dl className="space-y-2 text-sm"><EinsatzBerichtAnzeige einsatz={currentEinsatz} /></dl>
                  </div>
                  <EinsatzTruppsBericht einsatzId={currentEinsatz.id} einsatzName={currentEinsatz.titel} einsatznummer={currentEinsatz.einsatznummer} api={api} />
                </>
              )}
              {!isDetail && (
                <>
                  {form.typ === 'EINSATZ' && (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                      <div className="min-w-0">
                        <label className="label">Alarmierungszeit</label>
                        <Input type="datetime-local" value={form.alarmierungszeit} onChange={e => setForm({...form, alarmierungszeit: e.target.value})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Abfahrt Wache</label>
                        <Input type="datetime-local" value={form.abfahrt_zeit} onChange={e => setForm({...form, abfahrt_zeit: e.target.value})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Ankunft Einsatzort</label>
                        <Input type="datetime-local" value={form.ankunft_zeit} onChange={e => setForm({...form, ankunft_zeit: e.target.value})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Einsatzende</label>
                        <Input type="datetime-local" value={form.einsatzende_zeit} onChange={e => setForm({...form, einsatzende_zeit: e.target.value})} className="w-full" />
                      </div>
                      <div className="min-w-0">
                        <label className="label">Einsatzbereitschaft wiederhergestellt</label>
                        <Input type="datetime-local" value={form.einsatzbereit_zeit} onChange={e => setForm({...form, einsatzbereit_zeit: e.target.value})} className="w-full" />
                      </div>
                    </div>
                  )}
                  {form.typ !== 'EINSATZ' && <p className="text-xs text-dark-text-subtle">Phasenzeiten gibt es nur bei Einsätzen; bei Übungen sind sie nur lesend in der Ansicht sichtbar.</p>}
                  <EinsatzBerichtFelder werte={bericht} onChange={setBericht} />
                </>
              )}
            </>
          )}

          {reiter === 'kraefte' && (
            <>
              {/* Drohnen im Einsatz (Zuordnung, keine Pflicht) */}
              {!isCreate && currentEinsatz && <EinsatzDrohnen einsatzId={currentEinsatz.id} api={api} bearbeitbar={isEdit} />}

              {/* Zugeordnete Einsatzkräfte aus dem Kräfte Management */}
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-dark-text">
                  <Users className="h-4 w-4 text-primary-400" /> Zugeordnete Einsatzkräfte
                </h3>
                {kmLaedt ? (
                  <p className="mt-2 text-xs text-dark-text-muted">Lade Kräfte…</p>
                ) : kmFehler ? (
                  <p className="mt-2 text-xs text-red-300">{kmFehler}</p>
                ) : zugeordnete.length === 0 ? (
                  <p className="mt-2 text-xs text-dark-text-subtle">
                    Keine Einsatzkräfte zugeteilt. Die Zuordnung erfolgt im Kräfte Management.
                  </p>
                ) : (
                  <div className="mt-3 overflow-hidden rounded-xl border border-dark-border">
                    <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(96px,150px)_minmax(96px,150px)] gap-2 bg-dark-bg-tertiary px-3 py-2 text-xs font-semibold uppercase tracking-wider text-dark-text-subtle">
                      <span>Einsatzkraft</span><span>Einsatzbeginn</span><span>Einsatzende</span>
                    </div>
                    {zugeordnete.map(z => {
                      const zt = kmZeitenState[z.nutzerId] ?? { beginn: '', ende: '' }
                      const setz = (feld: 'beginn' | 'ende', wert: string) => setKmZeitenState(prev => ({
                        ...prev,
                        [z.nutzerId]: { ...(prev[z.nutzerId] ?? { beginn: '', ende: '' }), [feld]: wert },
                      }))
                      return (
                        <div key={z.nutzerId} className={`grid grid-cols-[minmax(0,1.6fr)_minmax(96px,150px)_minmax(96px,150px)] items-center gap-2 border-t border-dark-border px-3 py-2${z.bereich === 'einsatzende' && zt.ende ? ' bg-dark-bg-tertiary/40' : ''}`}>
                          <div className="min-w-0">
                            <div className="truncate text-sm text-dark-text">{kraftName(z.nutzerId)}</div>
                            <div className="text-xs text-dark-text-subtle break-words">
                              {z.bereich === 'besatzung' ? (z.fahrzeugId ? `Fahrzeugbesatzung · ${fahrzeugNamen[z.fahrzeugId] ?? `Fahrzeug #${z.fahrzeugId}`}` : 'Fahrzeugbesatzung') + (kmTruppText(kmData, z.nutzerId) ? ` · ${kmTruppText(kmData, z.nutzerId)}` : '')
                                : z.bereich === 'reserve' ? 'Reserve Wache'
                                : z.bereich === 'einsatzende' ? (zt.ende ? 'Einsatzende – beendet' : 'Einsatzende')
                                : 'Einsatzreserve'}
                            </div>
                          </div>
                          <Input type="time" value={zt.beginn} disabled={isDetail} onChange={e => setz('beginn', e.target.value)} />
                          <Input type="time" value={zt.ende} disabled={isDetail} onChange={e => setz('ende', e.target.value)} />
                        </div>
                      )
                    })}
                  </div>
                )}
                {!isDetail && zugeordnete.length > 0 && (
                  <p className="mt-2 text-xs text-dark-text-subtle">
                    Zeiten können erfasst oder geändert werden – sie werden mit dem Kräfte Management geteilt.
                  </p>
                )}
              </div>
            </>
          )}

          {reiter === 'fluege' && !isCreate && currentEinsatz && (
            <EinsatzFluege fluege={fluege} laedt={flugLaedt} fehler={flugFehler}
              drohneLabel={id => (id ? (drohnenNamen?.[id] ?? `Drohne #${id}`) : '—')}
              pilotLabel={id => (id ? kraftName(id) : '—')}
              onOeffnen={undefined} />
          )}

          {reiter === 'risiko' && !isCreate && currentEinsatz && (
            <EinsatzRisikobewertung einsatzId={currentEinsatz.id} listenPfad={`/risikobewertungen?einsatz_id=${currentEinsatz.id}`} neuLink={`/risikobewertung?einsatz=${currentEinsatz.id}`} />
          )}
        </div>
      </Card>
    </div>
  )
}
